import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import type { ResourceSeries, ResourceSermon } from '../../../hooks/useAdminSeries';
import { AssetUploaderPanel } from './AssetUploaderPanel';

interface Props {
  series: ResourceSeries;
  sermons: ResourceSermon[];
  onCreateSermon: (seriesId: string, fields: Partial<ResourceSermon>) => Promise<ResourceSermon>;
  onUpdateSermon: (id: string, fields: Partial<ResourceSermon>) => Promise<void>;
  onDeleteSermon: (id: string) => Promise<void>;
  onUploadAsset: (seriesId: string, sermonId: string | null, file: File, label: string, assetType: 'graphic' | 'social' | 'slide' | 'outline') => Promise<void>;
  onClose: () => void;
}

interface SermonFormState {
  id?: string;
  title: string;
  scripture: string;
  sermon_notes: string;
  sermon_order: number;
}

const EMPTY_FORM = (order: number): SermonFormState => ({
  title: '', scripture: '', sermon_notes: '', sermon_order: order,
});

export function SermonEditorModal({
  series, sermons, onCreateSermon, onUpdateSermon, onDeleteSermon, onUploadAsset, onClose
}: Props) {
  const [localSermons, setLocalSermons] = useState<ResourceSermon[]>(sermons);
  const [form, setForm] = useState<SermonFormState>(EMPTY_FORM(sermons.length + 1));
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { setLocalSermons(sermons); }, [sermons]);

  const startEdit = (s: ResourceSermon) => {
    setEditingId(s.id);
    setForm({ id: s.id, title: s.title, scripture: s.scripture ?? '', sermon_notes: s.sermon_notes ?? '', sermon_order: s.sermon_order });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(EMPTY_FORM(localSermons.length + 1));
  };

  const handleSave = async () => {
    if (!form.title.trim()) { setError('Title is required'); return; }
    setSaving(true);
    setError(null);
    try {
      if (editingId) {
        await onUpdateSermon(editingId, {
          title: form.title, scripture: form.scripture || null,
          sermon_notes: form.sermon_notes || null, sermon_order: form.sermon_order,
        });
        setLocalSermons(prev => prev.map(s => s.id === editingId ? { ...s, ...form } : s));
        setEditingId(null);
      } else {
        const created = await onCreateSermon(series.id, {
          title: form.title, scripture: form.scripture || null,
          sermon_notes: form.sermon_notes || null, sermon_order: form.sermon_order,
        });
        setLocalSermons(prev => [...prev, created]);
      }
      setForm(EMPTY_FORM(localSermons.length + 2));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this sermon?')) return;
    setDeletingId(id);
    try {
      await onDeleteSermon(id);
      setLocalSermons(prev => prev.filter(s => s.id !== id));
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white dark:bg-charcoal rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-sand/20 dark:border-slate/20">
          <div>
            <h2 className="text-base font-semibold text-midnight dark:text-soft-white">Manage Sermons</h2>
            <p className="text-xs text-slate dark:text-soft-white/50 mt-0.5">{series.title}</p>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-sand/20 dark:hover:bg-slate/20">
            <X className="h-4 w-4 text-slate" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 px-6 py-4 space-y-4">
          {/* Existing sermons */}
          {localSermons.length > 0 && (
            <div className="space-y-2">
              {localSermons.map(s => (
                <div key={s.id} className="border border-sand/20 dark:border-slate/20 rounded-lg overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-2.5 bg-sand/10 dark:bg-slate/10">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-xs font-semibold text-electric w-5 shrink-0">{s.sermon_order}</span>
                      <span className="text-sm font-medium text-midnight dark:text-soft-white truncate">{s.title}</span>
                      {s.scripture && <span className="text-xs text-slate dark:text-soft-white/50 shrink-0">{s.scripture}</span>}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        onClick={() => setExpandedId(expandedId === s.id ? null : s.id)}
                        className="p-1.5 rounded hover:bg-sand/20 dark:hover:bg-slate/20 text-slate"
                        title="Upload assets"
                      >
                        {expandedId === s.id ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      </button>
                      <button onClick={() => startEdit(s)} className="p-1.5 rounded hover:bg-sand/20 dark:hover:bg-slate/20 text-slate" title="Edit">
                        <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                      </button>
                      <button onClick={() => handleDelete(s.id)} disabled={deletingId === s.id}
                        className="p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-900/20 text-red-400">
                        {deletingId === s.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  {expandedId === s.id && (
                    <div className="px-4 py-3 border-t border-sand/20 dark:border-slate/20">
                      <AssetUploaderPanel seriesId={series.id} sermonId={s.id} onUpload={onUploadAsset} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Series-level assets */}
          <div className="border border-sand/20 dark:border-slate/20 rounded-lg p-4">
            <p className="text-xs font-semibold text-slate dark:text-soft-white/50 uppercase tracking-wide mb-3">Series-Level Assets</p>
            <AssetUploaderPanel seriesId={series.id} sermonId={null} onUpload={onUploadAsset} />
          </div>

          {/* Add / Edit form */}
          <div className="border border-electric/30 rounded-lg p-4 space-y-3">
            <p className="text-xs font-semibold text-electric uppercase tracking-wide">
              {editingId ? 'Editing Sermon' : 'Add Sermon'}
            </p>
            {error && <p className="text-sm text-red-500">{error}</p>}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-slate dark:text-soft-white/60 mb-1">Title *</label>
                <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-sand/40 dark:border-slate/30 rounded-lg bg-white dark:bg-midnight/50 text-midnight dark:text-soft-white"
                  placeholder="Sermon title" />
              </div>
              <div>
                <label className="block text-xs text-slate dark:text-soft-white/60 mb-1">Scripture</label>
                <input value={form.scripture} onChange={e => setForm(f => ({ ...f, scripture: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-sand/40 dark:border-slate/30 rounded-lg bg-white dark:bg-midnight/50 text-midnight dark:text-soft-white"
                  placeholder="e.g. John 3:16" />
              </div>
            </div>
            <div className="grid grid-cols-4 gap-3">
              <div className="col-span-1">
                <label className="block text-xs text-slate dark:text-soft-white/60 mb-1">Order</label>
                <input type="number" min={1} value={form.sermon_order} onChange={e => setForm(f => ({ ...f, sermon_order: Number(e.target.value) }))}
                  className="w-full px-3 py-2 text-sm border border-sand/40 dark:border-slate/30 rounded-lg bg-white dark:bg-midnight/50 text-midnight dark:text-soft-white" />
              </div>
              <div className="col-span-3" />
            </div>
            <div>
              <label className="block text-xs text-slate dark:text-soft-white/60 mb-1">Sermon Notes (HTML)</label>
              <textarea value={form.sermon_notes} onChange={e => setForm(f => ({ ...f, sermon_notes: e.target.value }))}
                rows={4}
                className="w-full px-3 py-2 text-sm border border-sand/40 dark:border-slate/30 rounded-lg bg-white dark:bg-midnight/50 text-midnight dark:text-soft-white font-mono resize-none"
                placeholder="<p>Sermon content here...</p>" />
            </div>
            <div className="flex gap-2 justify-end">
              {editingId && (
                <button onClick={cancelEdit} className="px-3 py-1.5 text-sm border border-sand/40 dark:border-slate/30 rounded-lg hover:bg-sand/10 text-midnight dark:text-soft-white">
                  Cancel
                </button>
              )}
              <button onClick={handleSave} disabled={saving}
                className="flex items-center gap-2 px-3 py-1.5 bg-electric text-white text-sm rounded-lg hover:bg-electric/90 disabled:opacity-50">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                {editingId ? 'Save Changes' : <><Plus className="h-4 w-4" />Add Sermon</>}
              </button>
            </div>
          </div>
        </div>

        <div className="flex justify-end px-6 py-4 border-t border-sand/20 dark:border-slate/20">
          <button onClick={onClose} className="px-4 py-2 text-sm bg-sand/20 dark:bg-slate/20 rounded-lg hover:bg-sand/30 text-midnight dark:text-soft-white">
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
